import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import { useParams, Link } from 'react-router-dom';
import { Box, Menu, Grid, Typography, Divider, MenuItem, Tooltip, styled } from '@mui/material';
import { useMenu } from '../../../components/base/useMenu';
import { formatDate } from '../../../utils/formateDate';
import useAwaitModal from '../../../hooks/useAwaitModal';
import TestOverviewModal from './TestOverviewModal';
import { useTheme } from '@emotion/react';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: 300,
    borderRadius: '12px',
    boxShadow: theme.shadows[4],
    padding: theme.spacing(1.5, 0),
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      right: 14,
      top: -8,
      width: 16,
      height: 16,
      backgroundColor: theme.palette.background.paper,
      transform: 'rotate(45deg)',
      zIndex: 0
    }
  }
}));

const DetailGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1, 2)
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
  fontWeight: 400
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.8rem',
  wordBreak: 'break-word',
  color: theme.palette.text.primary
}));

const ActionMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  '&.Mui-disabled': {
    opacity: 0.5
  }
}));

const formatTimeFromTimestamp = (timestamp) => {
  if (!timestamp) return;
  const dateObj = new Date(timestamp);
  return dateObj.toLocaleTimeString('en-GB', { hour12: false });
};

export const formatDateFromTimestamp = (timestamp) => {
  if (!timestamp) return;
  const dateObj = new Date(timestamp);
  return dateObj.toLocaleDateString('en-CA'); // YYYY-MM-DD format
};

const TestDetails = ({
  os,
  browser,
  savedTest,
  previousRun,
  testInfo,
  silentRefetchTest,
  url,
  title
}) => {
  const { triggerProps, menuProps } = useMenu();
  const { projectId, testId, batchId } = useParams();
  const theme = useTheme();

  const [requestTestOverviewModal, { open: testOverviewOpen, onClose: onCloseTestOverview }] =
    useAwaitModal();

  return (
    <Box>
      <Tooltip title="Test details" arrow>
        <IconButton
          size="medium"
          aria-controls={menuProps.open ? 'test-details-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={menuProps.open ? 'true' : undefined}
          {...triggerProps}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}>
          <MoreVertIcon sx={{ color: theme.palette.text.secondary }} />
        </IconButton>
      </Tooltip>

      <StyledMenu
        id="test-details-menu"
        {...menuProps}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}>
        <Typography variant="subtitle2" sx={{ px: 2, mb: 1, fontWeight: 600 }}>
          Test Details
        </Typography>

        <DetailGrid container spacing={1}>
          <DetailGrid item xs={5}>
            <DetailLabel>Created By</DetailLabel>
          </DetailGrid>
          <DetailGrid item xs={7}>
            <DetailValue textTransform="capitalize">{testInfo?.data?.creator?.name}</DetailValue>
          </DetailGrid>

          <DetailGrid item xs={5}>
            <DetailLabel>Date Created</DetailLabel>
          </DetailGrid>
          <DetailGrid item xs={7}>
            <Tooltip title={formatDate(testInfo?.data?.created_at)} arrow>
              <DetailValue>{formatDateFromTimestamp(testInfo?.data?.created_at)}</DetailValue>
            </Tooltip>
          </DetailGrid>

          <DetailGrid item xs={5}>
            <DetailLabel>Last Modified</DetailLabel>
          </DetailGrid>
          <DetailGrid item xs={7}>
            <Tooltip title={formatDate(testInfo?.data?.updated_at)} arrow>
              <DetailValue>{formatDateFromTimestamp(testInfo?.data?.updated_at)}</DetailValue>
            </Tooltip>
          </DetailGrid>

          <DetailGrid item xs={5}>
            <DetailLabel>Last Run</DetailLabel>
          </DetailGrid>
          <DetailGrid item xs={7}>
            <Tooltip title={formatDate(testInfo?.data?.last_run_at)} arrow>
              <DetailValue>{formatDateFromTimestamp(testInfo?.data?.last_run_at)}</DetailValue>
            </Tooltip>
          </DetailGrid>
        </DetailGrid>

        {batchId && (
          <>
            <Divider sx={{ my: 1.5, borderColor: theme.palette.divider }} />

            <Typography variant="subtitle2" sx={{ px: 2, mb: 1, fontWeight: 600 }}>
              Run Configuration
            </Typography>

            <DetailGrid container spacing={1}>
              <DetailGrid item xs={5}>
                <DetailLabel>Source</DetailLabel>
              </DetailGrid>
              <DetailGrid item xs={7}>
                <DetailValue textTransform="capitalize">{previousRun?.source}</DetailValue>
              </DetailGrid>

              <DetailGrid item xs={5}>
                <DetailLabel>Run Config</DetailLabel>
              </DetailGrid>
              <DetailGrid item xs={7}>
                <DetailValue>
                  {`${testInfo?.data?.width}px × ${testInfo?.data?.height}px`}
                </DetailValue>
              </DetailGrid>

              <DetailGrid item xs={5}>
                <DetailLabel>OS</DetailLabel>
              </DetailGrid>
              <DetailGrid item xs={7}>
                <DetailValue>
                  {previousRun?.operating_system} {previousRun?.operating_system_version}
                </DetailValue>
              </DetailGrid>

              <DetailGrid item xs={5}>
                <DetailLabel>Browser</DetailLabel>
              </DetailGrid>
              <DetailGrid item xs={7}>
                <DetailValue>
                  {previousRun?.browser} {previousRun?.browser_version}
                </DetailValue>
              </DetailGrid>

              <DetailGrid item xs={5}>
                <DetailLabel>Date (UTC)</DetailLabel>
              </DetailGrid>
              <DetailGrid item xs={7}>
                <DetailValue>{formatDateFromTimestamp(previousRun?.started_at)}</DetailValue>
              </DetailGrid>

              <DetailGrid item xs={5}>
                <DetailLabel>Time (UTC)</DetailLabel>
              </DetailGrid>
              <DetailGrid item xs={7}>
                <DetailValue>{formatTimeFromTimestamp(previousRun?.started_at)}</DetailValue>
              </DetailGrid>
            </DetailGrid>
          </>
        )}

        <Divider sx={{ my: 1.5, borderColor: theme.palette.divider }} />

        <ActionMenuItem
          component={Link}
          disabled={!testId}
          to={`/projects/${projectId}/test-results/${testId}?page=1`}
          onClick={() => menuProps.onClose()}>
          <HistoryIcon fontSize="small" />
          <Typography variant="body2">View Runs History</Typography>
        </ActionMenuItem>

        <ActionMenuItem
          disabled={!testId}
          onClick={() => {
            requestTestOverviewModal();
            menuProps.onClose();
          }}>
          <VisibilityIcon fontSize="small" />
          <Typography variant="body2">Test Overview</Typography>
        </ActionMenuItem>
      </StyledMenu>

      <TestOverviewModal
        open={testOverviewOpen}
        onClose={onCloseTestOverview}
        testInfo={testInfo?.data}
        silentRefetchTest={silentRefetchTest}
        url={url}
        title={title}
      />
    </Box>
  );
};

export default TestDetails;
